import PatientIntakeNavigation from '@brands/Components/PatientIntakeNavigation/PatientIntakeNavigation';
import { useWindowSize } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { useSnackbar } from '@brands/hooks/useStackbar';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectCurrentPatientState } from '@brands/store/selectors/currentPatientState';
import { selectOrganization } from '@brands/store/selectors/organization';
import { selectPatientForm } from '@brands/store/selectors/patientForm';
import { selectPatient } from '@brands/store/selectors/selectedPatient';
import GenericSnackbar from '@brands/Utils/GenericSnackbar';
import { DEFAULT_PATIENT_LOCATION_STATE } from '@brands/Utils/localStorage';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { Button } from '@mui/material';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import Container from '../../../../../Components/Container/Container';
import {
  routeAfterClinicalData,
  useMedicalHistoryOptional,
} from '@brands/Dashboard/PatientForms/PatientForm/utils/clinicalEnablementFlow';
import styles from './styles.module.scss';

const ClinicalDataIntro = (): JSX.Element => {
  const screenSize = useWindowSize();
  const navigate = useNavigate();
  const roleNavigate = useRoleNavigate();
  const { values, selectedPatient, checkExternalForms } = useAppSelector(selectPatientForm);
  const { selectedPatient: currentPatient } = useAppSelector(selectPatient);

  const { snackbar, closeSnackbar } = useSnackbar();
  const { userInfo } = useAppSelector(selectAuth);
  const { currentOrganization } = useAppSelector(selectOrganization);
  const currentState = useAppSelector(selectCurrentPatientState);
  const selectedPatientId = Number(selectedPatient?.value);
  const intakePatientId =
    selectedPatient?.value !== undefined && selectedPatient?.value !== '' && Number.isFinite(selectedPatientId)
      ? selectedPatientId
      : userInfo.id;
  const isStaffActingForPatient =
    userInfo.role.name === UserRoleName.Admin ||
    userInfo.role.name === UserRoleName.SuperAdmin ||
    userInfo.role.name === UserRoleName.OrganizationAdmin ||
    userInfo.role.name === UserRoleName.Collector ||
    userInfo.role.name === UserRoleName.CareAdvisor;
  const intakePatientState = isStaffActingForPatient
    ? currentState[currentPatient?.id ?? selectedPatientId] ||
      currentPatient?.address?.state ||
      userInfo.address?.state ||
      DEFAULT_PATIENT_LOCATION_STATE
    : currentState[userInfo.id] || userInfo.address?.state || DEFAULT_PATIENT_LOCATION_STATE;

  const isMedicalFormsDone = useMemo(() => {
    return !(
      checkExternalForms.includes('patient_medical_history') &&
      checkExternalForms.includes('patient_medical_conditions') &&
      checkExternalForms.includes('patient_dental_history')
    );
  }, [checkExternalForms]);

  const bypassRequired = useMedicalHistoryOptional(values);

  // When the section is optional (a Clinical Enablement case with a photo or
  // file attached, or a patient who was never invited) the whole
  // medical-conditions/medical-history/dental-history section can be skipped
  // without answering anything. This leaves those records exactly as they
  // were (empty, for a brand-new patient) instead of writing "none"/"unknown"
  // placeholder answers, so going back into the section later shows genuinely
  // blank fields rather than pre-filled ones. Continue wherever the last step
  // of the section would have gone.
  const onSkipAllClinicalData = (): void => {
    roleNavigate(routeAfterClinicalData(values));
  };

  const onContinue = (): void => {
    if (isMedicalFormsDone) {
      roleNavigate('/clinical-data/recap', {
        state: {
          caseType: values.find((item) => item.form_question_id === 20)?.value.caseType as string,
          patientState: intakePatientState,
          userOrganization: currentOrganization.id,
          isIntakeProcess: true,
          patientId: intakePatientId,
        },
      });
    } else {
      roleNavigate('/clinical-data/step-one', {
        state: {
          caseType: values.find((item) => item.form_question_id === 20)?.value.caseType as string,
          patientState: intakePatientState,
          userOrganization: currentOrganization.id,
          patientId: intakePatientId,
        },
      });
    }
  };

  return (
    <Container
      isVisibleOnMobile
      containerStyle={{
        justifyContent: screenSize.width >= 564 ? 'center' : 'start',
        overflow: 'hidden',
      }}
      childrenStyle={{
        zIndex: '2',
        paddingTop: screenSize.width > 768 ? '40px' : '16px',
        paddingBottom: screenSize.width <= 768 ? '16px' : '40px',
      }}
      isSidenavVisible={false}
      isIntakeProcess
    >
      <div className={`fs-unmask ${styles.clinicalDataIntroContainer}`}>
        <PatientIntakeNavigation progress={16} />
        <form className={`${styles.formContainer}`}>
          {isMedicalFormsDone ? (
            <div className={styles.clinicalDataIntroWrapper}>
              <div className={styles.clinicalDataIntroTitle}>
                Let’s make sure we have the latest information about you
              </div>
              <div className={styles.clinicalDataIntroBody}>
                <div className={styles.clinicalDataIntroBodyText}>
                  We’re glad to have you back! To ensure we provide you with the best care, we’d like to quickly review
                  your medical and dental history to make sure everything is up to date.
                </div>
                <div className={styles.clinicalDataIntroBodyText}>
                  On the next screen, we’ll ask you a few questions about any changes to your health, lifestyle, or
                  dental habits. This helps us keep your care plan personalized and your smile healthy and bright.
                </div>
                {bypassRequired && (
                  <div className={styles.clinicalDataIntroUploadedNotice} data-testid="clinical-data-uploaded-notice">
                    <TaskAltIcon className={styles.clinicalDataIntroUploadedNoticeIcon} />
                    <span>
                      You uploaded your records, so these questions are optional. Answer what you can, or skip ahead.
                    </span>
                  </div>
                )}
              </div>
              <div className={styles.clinicalDataIntroFooter}>
                <Button
                  variant="text"
                  data-testid="clinical-data-back-button"
                  className={styles.clinicalDataIntroFooterBack}
                  onClick={() => navigate(-1)}
                  onTouchStart={() => {
                    navigate(-1);
                  }}
                >
                  <ArrowBackIosNewIcon />
                  Back
                </Button>
                <div className={styles.clinicalDataIntroFooterActions}>
                  {bypassRequired && (
                    <Button
                      variant="text"
                      data-testid="clinical-data-skip-all-button"
                      className={styles.clinicalDataIntroFooterSkip}
                      type="button"
                      onClick={onSkipAllClinicalData}
                    >
                      Skip medical & dental history
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    data-testid="clinical-data-next-button"
                    className={styles.clinicalDataIntroFooterNext}
                    type="button"
                    onClick={onContinue}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.clinicalDataIntroWrapper}>
              <div className={styles.clinicalDataIntroTitle}>Let’s get to know you better</div>
              <div className={styles.clinicalDataIntroBody}>
                <div className={styles.clinicalDataIntroBodyText}>
                  We believe your oral health is a window to your overall well-being. To provide you with the best care,
                  we need to learn a little more about your dental and medical history.
                </div>
                <div className={styles.clinicalDataIntroBodyText}>
                  On the next few screens, we’ll ask you some quick questions about your health, lifestyle, and dental
                  habits. This helps us personalize your care plan and ensure your smile stays bright and healthy.
                </div>
              </div>
              <div className={styles.clinicalDataIntroFooter}>
                <Button variant="text" className={styles.clinicalDataIntroFooterBack} onClick={() => navigate(-1)}>
                  <ArrowBackIosNewIcon />
                  Back
                </Button>
                <div className={styles.clinicalDataIntroFooterActions}>
                  {bypassRequired && (
                    <Button
                      variant="text"
                      data-testid="clinical-data-skip-all-button"
                      className={styles.clinicalDataIntroFooterSkip}
                      type="button"
                      onClick={onSkipAllClinicalData}
                    >
                      Skip
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    className={styles.clinicalDataIntroFooterNext}
                    type="button"
                    onClick={onContinue}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
      <GenericSnackbar open={snackbar.isOpen} message={snackbar.message} type={snackbar.type} onClose={closeSnackbar} />
    </Container>
  );
};
export default ClinicalDataIntro;
